import React, { useState, useEffect, useContext } from "react";
import Button from "../../../utils/components/Button";
import DatePicker from "react-datepicker";
import Input from "../../../utils/components/Input";
import { AiOutlineCheck, AiOutlineClose } from "react-icons/ai";
import { Translate } from "react-localize-redux";
import { useOutletContext } from "react-router";
import { forwardRef } from "react";
import { getUnixTime } from "date-fns";
import toastr from "toastr";
import { useProject } from "../../../utils/hooks/useProject";
import API from "../../../utils/API";
import timeStampToDateFormat from "../../../utils/helpers/timestampToDateFormat";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../../utils/components/AlertModal";
import { RequestContext } from "../../../utils/context/RequestContext";

const days = [
  {
    id: 1,
    label: <Translate id="project_settings.general_settings.days.monday" />,
  },
  {
    id: 2,
    label: <Translate id="project_settings.general_settings.days.tuesday" />,
  },
  {
    id: 3,
    label: <Translate id="project_settings.general_settings.days.wednesday" />,
  },
  {
    id: 4,
    label: <Translate id="project_settings.general_settings.days.thursday" />,
  },
  {
    id: 5,
    label: <Translate id="project_settings.general_settings.days.friday" />,
  },
  {
    id: 6,
    label: <Translate id="project_settings.general_settings.days.saturday" />,
  },
  {
    id: 7,
    label: <Translate id="project_settings.general_settings.days.sunday" />,
  },
];

const dateTypeFormat = {
  name: "Full Date",
  format: "dd/MM/yyyy",
  showMonthYearPicker: false,
  showTimeSelect: false,
};

const GeneralSettings = () => {
  const [selectedDays, setSelectedDays] = useState([]);
  const [pickerDate, setPickerDate] = useState(new Date());
  const [dateType] = useState(dateTypeFormat);
  const [ferialDay, setFerialDay] = useState({});
  const [ferialDayList, setFerialDayList] = useState([]);

  const { project } = useProject();

  const [translate] = useOutletContext();

  const { setRequestLoading, requestLoading } = useContext(RequestContext);

  const DateCustomInput = forwardRef(({ value, onClick, headTitle }, ref) => (
    <button className="card px-5 w-auto me-3" onClick={onClick} ref={ref}>
      <span
        style={{
          position: "relative",
          marginTop: "-10px",
        }}
        className="title text-secondary bg-white mb-0 px-2 fw-bold"
      >
        {headTitle}
      </span>
      <span className="mb-0 ps-2 py-2">{value}</span>
    </button>
  ));

  useEffect(() => {
    if (!project) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate("select_a_project_alert")}
          />
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectedDays = (currentDay) => {
    let temp = selectedDays ? [...selectedDays] : [];
    if (temp?.includes(currentDay.id)) {
      temp.splice(temp.indexOf(currentDay.id), 1);
    } else {
      temp.push(currentDay.id);
    }
    sendSelectedDays(temp);
  };

  const sendSelectedDays = (selectedDayList) => {
    if (!project?.id || requestLoading) return;

    const payload = {
      days: selectedDayList,
    };

    setRequestLoading(true);

    API.putData(`workflow/settings/${project.id}`, payload).then(
      ({ data: res }) => {
        toastr.clear();
        toastr.success(res.message);
        setSelectedDays(selectedDayList);
      }
    );
  };

  const getSelectedDays = () => {
    if (!project?.id) return;

    API.getData(`workflow/settings/${project.id}`).then(({ data: res }) => {
      setSelectedDays(res?.data[0]?.days);
    });
  };

  const addFerialDay = () => {
    if (!project?.id || requestLoading) return;

    if (!ferialDay.explanation || !ferialDay.date) return;

    setRequestLoading(true);

    const payload = {
      explanation: ferialDay.explanation,
      date: getUnixTime(ferialDay.date),
    };

    API.postData(`workflow/vacation/${project.id}`, payload).then(
      ({ data: res }) => {
        toastr.clear();
        toastr.success(res.message);
        getFerialDays();
        setFerialDay({
          explanation: "",
        });
        setPickerDate(new Date());
      }
    );
  };

  const getFerialDays = () => {
    if (!project?.id || requestLoading) return;

    setRequestLoading(true);

    API.getData(`workflow/vacation/${project.id}`).then(({ data: res }) => {
      setFerialDayList(res.data);
    });
  };

  const removeFerialDay = (id) => {
    if (!project?.id || requestLoading) return;

    setRequestLoading(true);

    API.deleteData(`workflow/vacation/${project.id}?vacation_id=${id}`).then(
      () => {
        getFerialDays();
      }
    );
  };

  const handleChange = (e) => {
    setFerialDay({
      ...ferialDay,
      date: pickerDate,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    getFerialDays();
    getSelectedDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!project) return null;

  return (
    <div className="GeneralSettings row">
      <div className="col-12">
        <div className="row">
          <div className="col-12">
            <div className="title">
              {translate(
                "project_settings.general_settings.titles.work_week_settings"
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 sub-title-container">
            <div className="sub-title">
              {" "}
              {translate("project_settings.general_settings.titles.work_week")}
            </div>
            <p className="description">
              {translate(
                "project_settings.general_settings.descriptions.work_week"
              )}
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-12 day-buttons-container">
            <div className="day-buttons">
              {days.map((day) => (
                <Button
                  key={day.id}
                  onClick={() => handleSelectedDays(day)}
                  className={`day-btn ${
                    selectedDays?.includes(day.id)
                      ? "btn-orange text-light"
                      : "passive"
                  }`}
                >
                  {day.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12 sub-title-container">
            <div className="sub-title">
              {" "}
              {translate(
                "project_settings.general_settings.titles.ferial_days"
              )}
            </div>
            <p className="description">
              {translate(
                "project_settings.general_settings.descriptions.ferial_days"
              )}
            </p>
          </div>
        </div>

        <div className="row ferial-days">
          <div className="col-12">
            {ferialDayList.map((ferialDay, index) => (
              <div className="row align-items-center row-mb" key={index}>
                <div className="col-2">
                  <div className="date">
                    {timeStampToDateFormat(ferialDay.date, "dd/MM/yyyy")}
                  </div>
                </div>
                <div className="col-4">{ferialDay.explanation}</div>
                <div className="col-1">
                  <div
                    className="close-mark-holder d-flex align-items-center justify-content-center cursor--pointer"
                    onClick={(id) => removeFerialDay(ferialDay.id)}
                  >
                    <AiOutlineClose className="fw-bold" color="white" />
                  </div>
                </div>
              </div>
            ))}

            <div className="row">
              <div className="col-12">
                <div className="row">
                  <div className="col-2 sub-title-container">
                    <div className="sub-title date">
                      {translate(
                        "project_settings.general_settings.titles.add_ferial"
                      )}
                    </div>
                  </div>
                </div>
                <div className="row align-items-center add-ferial-day">
                  <div className="col-12 d-flex justify-content-start align-items-end">
                    <DatePicker
                      className="picker"
                      selected={pickerDate}
                      dateFormat={dateType.format}
                      showMonthYearPicker={dateType.showMonthYearPicker}
                      showTimeSelect={dateType.showTimeSelect}
                      onChange={(date) => setPickerDate(date)}
                      customInput={<DateCustomInput headTitle={"Date"} />}
                    />

                    <Input
                      type="text"
                      name="explanation"
                      value={ferialDay.explanation}
                      placeholder={translate(
                        "project_settings.general_settings.placeholders.enter_ferial_day_description"
                      )}
                      onChange={handleChange}
                      className="w-100"
                    />

                    <div
                      className="check-mark-holder d-flex align-items-center justify-content-center cursor--pointer ms-2"
                      onClick={addFerialDay}
                    >
                      <AiOutlineCheck className="fw-bold" color="white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;
